import { forwardRef, PropsWithoutRef, Suspense, useImperativeHandle, useState } from 'react';
import {
    experimentalValidateWithZod,
    FormTextareaField,
    lt,
    showInfoAlert,
    showSuccessAlert,
    useFeatureFlag,
    useFormControls,
} from '@mews/framework';
import { Card, Stack } from '@optimus-web/core';
import { AuthorizationType, PaymentCardFormat, PaymentState, useRepositoryMutation } from '@mews-commander/data-access';
import { Loader } from '@mews-commander/ui';
import { FeatureFlag } from '@mews-commander/enums';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { PaymentFormProps } from '../types';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { useDefaultFormContext } from '../useDefaultFormValues';
import {
    useInvalidatePaymentsTableRecordsQueries,
    useInvalidatePaymentMethodsTableRecordsQuery,
    useGetAccountingSurchargeFeeSuspense,
    useInvalidatePreauthorizationTableRecordQuery,
    useTokenizeCard,
    useGetPaymentMethodsSuspense,
} from '../../../../queries';
import { isNewCard } from '../../../../utils';
import { AuthorizationTypeSegmentControl } from '../AuthorizationTypeSegmentTabs/AuthorizationTypeSegmentControl';
import { PaymentMethodSelectField } from '../PaymentMethodSelectField/PaymentMethodSelectField';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import {
    useAddGatewayCardPayment,
    useGetChargeablePreauthorizationsSuspense,
    useGetSupportedGatewayCardTypesSuspense,
} from '../queries';
import { TokenizeCardFormField } from '../TokenizeCardForm/schema';
import { TokenizeCardFormFields } from '../TokenizeCardForm/TokenizeCardFormFields';
import { useInvalidateGetPaymentPlansTableRecords } from '../../../tables/PaymentPlans/queries';
import { SurchargeFeeCheckbox } from './SurchargeFeeCheckbox';
import { areNewPaymentCardFormValues, hasPaymentMethodId, hasPreAuthorizationId } from './utils';
import {
    createGatewayPaymentForNewCardSchema,
    createGatewayPaymentForPreauthSchema,
    GatewayPaymentFormField,
    GatewayPaymentFormValues,
    gatewayPaymentForSelectedCardSchema,
} from './schema';

export const GatewayPaymentForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const isPaymentPlanEnabled = useFeatureFlag(FeatureFlag.paymentPlanEnabled);
        const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();
        const invalidatePreauthorizationRecords = useInvalidatePreauthorizationTableRecordQuery();
        const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();
        const invalidateGetPaymentPlansTableRecords = useInvalidateGetPaymentPlansTableRecords();
        const {
            data: { surchargeServiceId },
        } = useGetAccountingSurchargeFeeSuspense();

        const {
            data: { paymentCards, sepas },
        } = useGetPaymentMethodsSuspense({ accountId });

        const { data: chargeablePreauthorizations } = useGetChargeablePreauthorizationsSuspense({
            accountId,
            isGateway: true,
        });
        const hasChargeablePreAuthorization = chargeablePreauthorizations.length > 0;

        const {
            data: { supportedPaymentCardTypes },
        } = useGetSupportedGatewayCardTypesSuspense();

        const tokenizeCard = useTokenizeCard({ accountId });
        const addGatewayCardPayment = useAddGatewayCardPayment({ accountId });
        const { mutateAsync: mutateAddSepa } = useRepositoryMutation('sepaDirectDebitPayments', 'add');

        const { defaultFormValues: defaultValues } = useDefaultFormContext();
        const [activeAuthorizationTypeSegment, setActiveAuthorizationTypeSegment] = useState<AuthorizationType>(
            hasChargeablePreAuthorization ? AuthorizationType.Preauthorization : AuthorizationType.CreditCardPayment
        );
        const { renderFormContent, submit, hasDirtyFields, watch, setValue } =
            useFormControls<GatewayPaymentFormValues>({
                onSubmit: async (values) => {
                    try {
                        onSubmitting?.();
                        let paymentCardId = hasPaymentMethodId(values) ? values.paymentMethodId : '';
                        let cardLastDigits = paymentCards?.find((p) => p.id === paymentCardId)?.lastDigits;
                        const preauthorizationId = hasPreAuthorizationId(values) ? values.preAuthorizationId : '';

                        if (areNewPaymentCardFormValues(values)) {
                            const { id, lastDigits } = await tokenizeCard(values);

                            paymentCardId = id;
                            cardLastDigits = lastDigits;
                            setValue(GatewayPaymentFormField.PaymentMethodId, id);
                            invalidatePaymentMethods({ customerId: accountId });

                            showSuccessAlert({
                                description: lt('CardCreatedSuccessMessage', { cardLastDigits: lastDigits ?? '' }),
                            });
                        }

                        const { currencyCode: currency, amount, serviceOrderId, paymentPlanId } = values;
                        const sepaPaymentMethod = sepas?.find(({ id }) => id === paymentCardId);
                        if (sepaPaymentMethod) {
                            await mutateAddSepa({
                                accountId,
                                sepaProviderId: sepaPaymentMethod.id,
                                amount: {
                                    currencyCode: currency,
                                    value: amount,
                                },
                                serviceOrderId,
                                paymentPlanId,
                            });

                            showSuccessAlert({
                                description: lt('SepaDirectDebitSuccessMessage', {
                                    lastFourIbanDigits: sepaPaymentMethod.lastDigits ?? '',
                                    amount: localizeCurrencyValue({ value: amount, currency }),
                                }),
                            });
                        } else {
                            const { state } = await addGatewayCardPayment(values, paymentCardId, preauthorizationId);

                            switch (state) {
                                case PaymentState.Charged:
                                    showSuccessAlert({
                                        description: lt('CardPaymentSuccessMessage', {
                                            cardLastDigits: cardLastDigits ?? '',
                                            amount: localizeCurrencyValue({ value: amount, currency }),
                                        }),
                                    });
                                    break;
                                case PaymentState.Verifying:
                                    showInfoAlert({
                                        description: lt('VerifyingPaymentResultMessage'),
                                    });
                                    break;
                                default:
                                    break;
                            }
                        }

                        invalidatePreauthorizationRecords({ accountId });
                        invalidatePaymentsTableRecordsQueries({ accountId });
                        invalidateGetPaymentPlansTableRecords(paymentPlanId);

                        onSuccess?.();
                    } finally {
                        onSubmitted?.();
                    }
                },
                defaultValues: {
                    ...defaultValues,
                    [GatewayPaymentFormField.ApplyPaymentCardSurcharge]: !!(
                        activeAuthorizationTypeSegment === AuthorizationType.CreditCardPayment && surchargeServiceId
                    ),
                    [TokenizeCardFormField.PaymentCardFormat]: PaymentCardFormat.Physical,
                },
                resolver: (values) => {
                    let schema;
                    if (hasPreAuthorizationId(values)) {
                        const preauthorizationAmount = chargeablePreauthorizations.find(
                            ({ id }) => id === values.preAuthorizationId
                        )?.amount.value;
                        schema = createGatewayPaymentForPreauthSchema(preauthorizationAmount ?? 0);
                    } else if (
                        hasPaymentMethodId(values) &&
                        isNewCard(values[GatewayPaymentFormField.PaymentMethodId])
                    ) {
                        schema = createGatewayPaymentForNewCardSchema(supportedPaymentCardTypes);
                    } else {
                        schema = gatewayPaymentForSelectedCardSchema;
                    }
                    return experimentalValidateWithZod(schema)(values);
                },
            });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );
        return renderFormContent(
            <Stack spacing="300" vertical>
                <PaymentCommonSection />
                {hasChargeablePreAuthorization ? (
                    <AuthorizationTypeSegmentControl
                        activeAuthorizationTypeSegment={activeAuthorizationTypeSegment}
                        setActiveAuthorizationTypeSegment={setActiveAuthorizationTypeSegment}
                        accountId={accountId}
                        isExternal={false}
                        cardPaymentItemLabel={lt('OnlinePayment')}
                    />
                ) : (
                    <PaymentMethodSelectField
                        required
                        name={GatewayPaymentFormField.PaymentMethodId}
                        label={lt(
                            isPaymentPlanEnabled ? 'ProcessPaymentMewsPaymentsPaymentMethodSelectLabel' : 'PaymentCard'
                        )}
                        helper={lt('ProcessPaymentMewsPaymentsCardPaymentCardSelectionHint')}
                        accountId={accountId}
                        isExternal={false}
                    />
                )}

                {activeAuthorizationTypeSegment === AuthorizationType.CreditCardPayment &&
                isNewCard(watch(GatewayPaymentFormField.PaymentMethodId)) ? (
                    <Card padding="300" outline>
                        <Suspense fallback={<Loader />}>
                            <TokenizeCardFormFields />
                        </Suspense>
                    </Card>
                ) : null}
                <PaymentAmountSection
                    isCurrencyFieldDisabled={activeAuthorizationTypeSegment === AuthorizationType.Preauthorization}
                />
                <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={1} />
                {activeAuthorizationTypeSegment === AuthorizationType.CreditCardPayment && surchargeServiceId ? (
                    <SurchargeFeeCheckbox accountId={accountId} />
                ) : null}
            </Stack>
        );
    }
);
